import seek, { seekTypes } from '@adc/seek'
import type { CollectionResult } from '@adc/sdk/types'
import * as blueprint from '@adc/blueprint'
import { QueryType, Coordinates, ListingCarouselFilters } from './'
import { match } from 'ts-pattern'

type FetchResult = {
  data: CollectionResult
  errors: Record<string, unknown>[]
}

/**
 * Simple DSL for specific showcase listings
 * @param listingIds
 * @returns
 */
const getDSLForShowcaseListings = (listingIds: string[]) => ({
  equals: {
    fields: 'listing_id.keyword',
    values: listingIds,
  },
})

const getDSL = (queryType: QueryType) =>
  /**
   * For simplicity, we are assuming that all of these start with an "and" expression at the top level,
   * which makes it easier to add additional expressions.
   */
  match(queryType)
    .with('all', () => ({
      and: {
        expressions: [{ all: {} }],
      },
    }))
    .with('land', () => ({
      and: {
        expressions: [
          {
            equals: {
              fields: 'primary_property_summary.structure_type_code.keyword',
              values: ['LAND'],
            },
          },
        ],
      },
    }))
    .with('noLand', () => ({
      and: {
        expressions: [
          {
            not: {
              expression: {
                equals: {
                  fields:
                    'primary_property_summary.structure_type_code.keyword',
                  values: ['LAND'],
                },
              },
            },
          },
        ],
      },
    }))
    .with('remoteBid', () => ({
      and: {
        expressions: [
          {
            or: {
              expressions: [
                {
                  equals: {
                    fields: 'listing_summary.is_remote_bid_enabled',
                    values: [true],
                  },
                },
                {
                  equals: {
                    fields:
                      'listing_summary.is_remote_before_and_during_auction_enabled',
                    values: [true],
                  },
                },
              ],
            },
          },
        ],
      },
    }))
    .with('firstLook', () => ({
      and: {
        expressions: [
          {
            equals: {
              fields: 'listing_configuration.is_first_look_enabled',
              values: [true],
            },
          },
        ],
      },
    }))
    .with('privateSellerLand', () => ({
      and: {
        expressions: [
          {
            equals: {
              fields: 'listing_configuration.product_type.keyword',
              values: ['PRIVATE_SELLER'],
            },
          },
          {
            equals: {
              fields: 'primary_property_summary.structure_type_code.keyword',
              values: ['LAND'],
            },
          },
        ],
      },
    }))
    .with('privateSellerNoLand', () => ({
      and: {
        expressions: [
          {
            equals: {
              fields: 'listing_configuration.product_type.keyword',
              values: ['PRIVATE_SELLER'],
            },
          },
          {
            not: {
              expression: {
                equals: {
                  fields:
                    'primary_property_summary.structure_type_code.keyword',
                  values: ['LAND'],
                },
              },
            },
          },
        ],
      },
    }))
    .with('privateSellerAll', () => ({
      and: {
        expressions: [
          {
            equals: {
              fields: 'listing_configuration.product_type.keyword',
              values: ['PRIVATE_SELLER'],
            },
          },
        ],
      },
    }))
    .otherwise(() => ({
      and: {
        expressions: [{ all: {} }],
      },
    }))

type FieldConfig = Parameters<seek['fields']>[1]

const fieldsConfig: FieldConfig = {
  fragmentDefinitions: {
    ListingSearchFields: {
      fragment: blueprint.listing.fragments.listingCard.definition,
    },
  },
}

const DEFAULT_PAGE_SIZE = 20
const DEFAULT_OFFSET = 0

interface getMarketingListingsParams {
  queryType?: QueryType
  showcaseListings?: string[]
  coordinates: Coordinates
  filters: ListingCarouselFilters
}

const fragmentNameWithoutLeadingDots =
  blueprint.listing.fragments.listingCard.reference.replace(/^\.{3}/, '')

export const getMarketingListings = ({
  queryType,
  showcaseListings,
  coordinates,
  filters,
}: getMarketingListingsParams): Promise<FetchResult> => {
  let dsl
  if (queryType) {
    dsl = getDSL(queryType)
    // augment the basic dsl (assuming it's always an "and" expression)
    // @ts-ignore next line
    const expressions = dsl?.and?.expressions as Array<Record<string, unknown>> // eslint-disable-line
    if (expressions)
      expressions.push({
        not: {
          expression: {
            equals: {
              fields: 'listing_status_group.keyword',
              values: ['CANCELED'],
            },
          },
        },
      })
    if (filters?.state) {
      expressions.push({
        equals: {
          fields: 'seller_property.country_primary_subdivision.keyword',
          values: [filters.state],
        },
      })
    }
    if (filters?.tags) {
      expressions.push({
        equals: {
          fields: 'marketing_tags.tag.keyword',
          values: filters.tags,
        },
      })
    }
    if (filters?.productTypes) {
      expressions.push({
        equals: {
          fields: 'listing_configuration.product_type.keyword',
          values: filters.productTypes,
        },
      })
    }
  } else if (showcaseListings) {
    dsl = getDSLForShowcaseListings(showcaseListings)
  } else {
    throw new Error('queryType or showcaseListings is required')
  }

  const seeker = seek
    .for(seekTypes.ACTIVE_LISTINGS)
    .operationName('searchMarketingListings')
    // @ts-ignore dsl doesn't match type
    .dsl(dsl)
    .fields([`fragment:${fragmentNameWithoutLeadingDots}`], fieldsConfig)
    .size(DEFAULT_PAGE_SIZE)
    .offset(DEFAULT_OFFSET)

  /**
   * If not state-specific, sort by auction date
   */
  if (!filters?.state) {
    seeker.sortByGeo(
      'primary_property_summary.address.coordinates',
      {
        lat: coordinates.latitude,
        lon: coordinates.longitude,
      },
      'ASC'
    )
  }
  // fall-back sort
  seeker.sortByField('auction.auction_date', 'ASC')

  return seeker.execute().then(
    ({
      data,
      errors,
    }: {
      data: {
        seek: CollectionResult
      }
      errors: Record<string, unknown>[]
    }) => {
      const result = {
        data: data.seek,
        errors,
      }
      return result
    }
  )
}
